import { createContext, useContext, useMemo, type ReactNode } from "react";

import { useBrandLogo } from "@/hooks/useBrandLogo";
import { resolveDashboardLogo } from "@/lib/dashboardLogo";
import {
  useActiveBranding,
  useBrandingByHost,
  useBrandingByReferral,
  useBrandingBySlug,
  useShowBranding,
  type NetworkBranding,
} from "@/hooks/useNetworkBranding";

/** Brand shown on the current surface. PODGO unless a network white label applies. */
export interface BrandIdentity {
  name: string;
  logoLight: string;
  logoDark: string;
  /** True when a network's branding has replaced PODGO's on this surface. */
  isWhiteLabel: boolean;
  /** Whether the "Powered by PODGO" credit should be rendered. */
  showPoweredBy: boolean;
  /** Whether PODGO's marketing menu/footer should be hidden on branded pages. */
  hideMarketingMenu: boolean;
  /** True while the PODGO logo is still unknown; logo spots hold space instead of the wordmark. */
  logoPending: boolean;
  dashboardLogo: ReturnType<typeof resolveDashboardLogo>;
}

const BrandingContext = createContext<NetworkBranding | null>(null);

/**
 * Supplies white-label branding to every logo/wordmark spot below it. Surfaces
 * that must stay PODGO (marketing site, pricing, blog) simply render without a
 * provider.
 */
export function BrandingProvider({
  branding,
  children,
}: {
  branding: NetworkBranding | null | undefined;
  children: ReactNode;
}) {
  return (
    <BrandingContext.Provider value={branding ?? null}>{children}</BrandingContext.Provider>
  );
}

/** Portal and admin surfaces: branding follows whoever is signed in. */
export function ActiveBrandingProvider({ children }: { children: ReactNode }) {
  const { data } = useActiveBranding();
  const { data: host } = useBrandingByHost();
  return <BrandingProvider branding={data ?? host}>{children}</BrandingProvider>;
}

/** Public show/episode pages: branding follows the show's network. */
export function ShowBrandingProvider({
  showId,
  children,
}: {
  showId?: string | null;
  children: ReactNode;
}) {
  const { data } = useShowBranding(showId);
  const { data: host } = useBrandingByHost();
  return <BrandingProvider branding={data ?? host}>{children}</BrandingProvider>;
}

/**
 * Sign-in screen: branding follows the `/n/<slug>` link (or the legacy `?n=`
 * query), then the referrer's network on a `?ref=` link, then the network's
 * own custom domain.
 */
export function SlugBrandingProvider({
  slug,
  referralCode,
  children,
}: {
  slug?: string | null;
  referralCode?: string | null;
  children: ReactNode;
}) {
  const { data, isLoading: slugLoading } = useBrandingBySlug(slug);
  const { data: referral, isLoading: referralLoading } = useBrandingByReferral(slug ? null : referralCode);
  const { data: host, isLoading: hostLoading } = useBrandingByHost();

  // An unresolved network is not a PODGO page: hold the whole surface so its
  // logos, marketing menus and footer cannot briefly use the fallback brand.
  if (slugLoading || (!data && referralLoading) || (!data && !referral && hostLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" role="status" aria-label="Loading page">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  return <BrandingProvider branding={data ?? referral ?? host}>{children}</BrandingProvider>;
}

/**
 * The brand for the current surface. Falls back to the PODGO logos configured
 * in Theme Options and the built-in PODGO name.
 */
export function useBrandIdentity(): BrandIdentity {
  const network = useContext(BrandingContext);
  const { data: podgoLogos, pending } = useBrandLogo();

  return useMemo(() => {
    const light = (network?.logo_url_light ?? "").trim();
    const dark = (network?.logo_url_dark ?? "").trim();
    const name = (network?.name ?? "").trim();
    const isWhiteLabel = !!network && (!!name || !!light || !!dark);

    if (!isWhiteLabel) {
      return {
        name: "PODGO",
        logoLight: podgoLogos.light,
        logoDark: podgoLogos.dark,
        isWhiteLabel: false,
        showPoweredBy: false,
        hideMarketingMenu: false,
        logoPending: pending,
        dashboardLogo: resolveDashboardLogo(),
      };
    }

    return {
      name: name || "PODGO",
      // Either slot alone is enough; the other background reuses it.
      logoLight: light || dark,
      logoDark: dark || light,
      isWhiteLabel: true,
      showPoweredBy: !network?.hide_powered_by,
      hideMarketingMenu: network?.hide_marketing_menu ?? true,
      logoPending: false,
      dashboardLogo: resolveDashboardLogo(network),
    };
  }, [network, podgoLogos.light, podgoLogos.dark, pending]);
}
